import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/Input";
import { PublicPageFrame } from "../components/layout/PublicPageFrame";

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const [sesionExpiradaBanner] = useState(() => searchParams.get("sesion") === "expirada");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (sesionExpiradaBanner) {
      window.history.replaceState({}, "", "/login");
    }
  }, [sesionExpiradaBanner]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { redirectTo } = await login(email.trim(), password);
      navigate(redirectTo);
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : "Credenciales incorrectas";
      setError(message ?? "Credenciales incorrectas");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicPageFrame>
      <div className="bg-white/[0.92] backdrop-blur-[8px] rounded-2xl shadow-2xl p-8 w-full max-w-md border border-white/20">
        <h1 className="mb-6 text-xl font-semibold text-[#2C2C2C]">Iniciar sesión</h1>
        {sesionExpiradaBanner && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Tu sesión ha caducado. Vuelve a iniciar sesión para continuar.
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-[#2C2C2C]">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-[#2C2C2C] focus:border-[#1A3AB8] focus:outline-none focus:ring-1 focus:ring-[#1A3AB8]"
              placeholder="tu@email.com"
            />
          </div>
          <Input
            id="password"
            label="Contraseña"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-[#1A3AB8] px-4 py-2.5 font-medium text-white transition hover:bg-[#15308a] disabled:opacity-50"
          >
            {submitting ? "Entrando…" : "Iniciar sesión"}
          </button>
        </form>
        <p className="mt-4 text-center text-sm text-gray-600">
          ¿Primera vez?{" "}
          <Link to="/register" className="font-medium text-[#1A3AB8] hover:underline">
            Regístrate
          </Link>
        </p>
        <p className="mt-2 text-center text-sm text-gray-600">
          <Link to="/recuperar-password" className="text-[#1A3AB8] hover:underline">
            ¿Olvidaste tu contraseña?
          </Link>
        </p>
      </div>
    </PublicPageFrame>
  );
}
